import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  ScoredUserQuery,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import { managedShortcuts } from "@mirage/service-typeahead-search/service/utils/managed-shortcuts";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import { isScoredPeopleResult } from "@mirage/shared/search/people-recommendations";
import ResultType = typeahead.ResultType;

const logger = tagged("service-typeahead-search/search/utils");
const metrics = namespace("typeahead-search");

export function performCacheAnalysis(cache: TypeaheadCache) {
  const t0 = performance.now();
  const cacheAnalysis = getCacheAnalysis(cache);
  const t1 = performance.now();
  const cacheAnalysisMs = t1 - t0;
  if (cacheAnalysisMs > 200) {
    const rounded = Math.round(cacheAnalysisMs);
    logger.warn(
      `typeahead cache analysis time is unexpectedly high: ${rounded}ms`,
    );
  }
  metrics.stats("search/cacheAnalysis", t1 - t0, {
    name: "cacheAnalysis",
  });

  return cacheAnalysis;
}

export function getLimits(
  query: string,
  mode: typeahead.Mode,
  limit: number,
  config: typeahead.Config,
): [number, number | undefined] {
  if (query?.startsWith("/")) {
    if (!config.isOmniboxEnabled) {
      return [managedShortcuts.length, undefined];
    }
    return [9, managedShortcuts.length];
  }

  if (!config.isOmniboxEnabled) {
    return [limit, undefined];
  }

  const isNullState = !query.length;
  let totalLimit: number | undefined;
  let perSectionLimit: number | undefined;

  if (isNullState) {
    switch (mode) {
      case typeahead.Mode.Default:
        totalLimit = 9;
        perSectionLimit = 5;
        break;
      case typeahead.Mode.Find:
      case typeahead.Mode.Ask:
      case typeahead.Mode.Write:
        totalLimit = 8;
        perSectionLimit = 4;
        break;
      case typeahead.Mode.Organize:
        totalLimit = 1;
        perSectionLimit = 1;
        break;
    }
  } else {
    switch (mode) {
      case typeahead.Mode.Default:
        totalLimit = 9;
        perSectionLimit = 7;
        break;
      case typeahead.Mode.Write:
        totalLimit = 9;
        perSectionLimit = 5;
        break;
      case typeahead.Mode.Find:
      case typeahead.Mode.Ask:
        totalLimit = 9;
        perSectionLimit = undefined;
        break;
      case typeahead.Mode.Organize:
        totalLimit = 1;
        perSectionLimit = 1;
        break;
    }
  }

  // Use mode-specific limits or fallback to defaults
  return [totalLimit ?? 9, perSectionLimit];
}

// In the predicates some sources wish to be included/excluded based on the
// state of the local cache. For example: with an empty query, if the user
// has recent queries we want to the null state to show recent queries, but if
// the user doesn't have recent queries we want the null state to show
// "recommendations".
//
// WARN:
// We should keep the analysis to a minimum! If we need more, we probably need
// a better pattern for a source to be included based on the state of another
// source
export function getCacheAnalysis(
  cache: TypeaheadCache,
): typeahead.CacheAnalysis {
  // <1ms right now. We should implement cache.some() if perf becomes a concern
  const recentQueries = cache.all(CacheKey.RecentQueries) as PreviousQuery[];
  const hasRecentQueries = recentQueries.length > 0;
  return { hasRecentQueries };
}

export function isSuggestedQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.SuggestedQuery;
}

export function isAskUserQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.AskUserQuery;
}

export function isWriteUserQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.WriteUserQuery;
}

export function isOrganizeUserQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.OrganizeUserQuery;
}

export function isRecentQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.PreviousQuery;
}

export function isURLShortcut(result: typeahead.ScoredResult) {
  return result?.type === ResultType.URLShortcut;
}

export function isRecentChat(result: typeahead.ScoredResult) {
  return result?.type === ResultType.RecentChat;
}

export function isMyProfileSuggestion(result: typeahead.ScoredResult) {
  return result?.type === ResultType.MyProfileSuggestion;
}

export function sortScoredResults(isScoringSections: boolean) {
  return (
    left: typeahead.ScoredResult,
    right: typeahead.ScoredResult,
  ): number => {
    if (
      !isScoringSections ||
      (left.sectionId === undefined && right.sectionId === undefined)
    ) {
      return right.score - left.score;
    }

    if (left.sectionId === undefined) return -1;
    if (right.sectionId === undefined) return 1;

    if (left.sectionScore !== right.sectionScore) {
      return right.sectionScore - left.sectionScore;
    }

    return right.score - left.score;
  };
}

// These results have a URL parameter we want to dedupe on
export const isURLResult = (result: typeahead.ScoredResult) => {
  return (
    result.type === ResultType.SearchResult ||
    result.type === ResultType.Recommendation ||
    result.type === ResultType.StackItem
  );
};

export const getDedupeKey = (result: typeahead.ScoredResult) => {
  if (isScoredPeopleResult(result)) {
    return `${result.type}:${result.uuid}`;
  } else {
    return result.uuid;
  }
};

/**
 * Clamps the SuggestedQuery (the SERP CTA) to either the first or the second
 * position depending on it's score.
 *
 * In the unlikely case of a tie, the suggested query will be first
 *
 * Also clamps PeopleResults after SuggestedQuery in most cases.
 */
export function clampSomeResultsMaxPosition(
  userQuery: ScoredUserQuery | undefined,
  otherResults: typeahead.ScoredResult[],
  isUserQuerySticky?: boolean,
) {
  if (userQuery == null) {
    return otherResults;
  }

  if (userQuery.score <= 0.1 && !isUserQuerySticky) {
    // if the suggestedQuery score is at/below 0.1, it means we should keep the
    // suggestedQuery at the end of the list.
    // this isn't ideal, but since the suggestedQuery score is set by a growthbook flag
    // doing this avoids needing multiple feature flags for this one feature
    // while still allowing experimentation with a control group
    return [...otherResults, userQuery];
  }

  if (userQuery.score >= (otherResults[0]?.score ?? 0) || isUserQuerySticky) {
    // if the suggestedQuery is the highest scoring result (or it's a tie), then suggested query is first
    return [userQuery, ...otherResults];
  }

  // otherwise it's second, followed by people results
  return [...otherResults.slice(0, 1), userQuery, ...otherResults.slice(1)];
}

export function filterAndMergeByType<T extends typeahead.ScoredResult>(
  results: typeahead.ScoredResult[],
  orderedTypes: typeahead.ResultType[],
) {
  const typePriority = new Map(
    orderedTypes.map((type, index) => [type, index]),
  );

  return results
    .filter((result): result is T => typePriority.has(result.type))
    .sort((a, b) => {
      const aIndex = typePriority.get(a.type) ?? -1;
      const bIndex = typePriority.get(b.type) ?? -1;
      return aIndex !== bIndex ? aIndex - bIndex : b.score - a.score;
    });
}

export const getDynamicSectionLimit = (
  priorityResults: typeahead.ScoredResult[],
  sectionLimit?: number,
) => {
  if (priorityResults.length && sectionLimit) return sectionLimit - 1;
  return sectionLimit;
};

export function orderedSectionGrouping(
  results: typeahead.ScoredResult[],
): typeahead.ScoredResult[][] {
  if (results.length === 0) return [];

  const sections: typeahead.ScoredResult[][] = [];
  const sectionIndexMap = new Map<string, number>();

  for (const result of results) {
    const sectionId = result.sectionId || "unknown";
    let sectionIndex = sectionIndexMap.get(sectionId);

    if (sectionIndex === undefined) {
      sectionIndex = sections.length;
      sectionIndexMap.set(sectionId, sectionIndex);
      sections.push([]);
    }

    sections[sectionIndex].push(result);
  }

  return sections;
}
